import { useMemo } from 'react';
import { usePricing } from '../../contexts/PricingContext';
import { OBJECT_TYPES, GRID_CELL_SIZE_M, FRAMELESS_GRID_CELL_SIZE_M, STAIRS_COUNTERPART_MAP } from '../configuratorConstants';
import { useTranslation } from 'react-i18next';
import { distance } from '../configuratorUtils';

// Значение по умолчанию для стоимости одной клетки (квадратного метра) для безкаркасных проектов
// Используется только если цена не задана в админ-панели
const DEFAULT_FRAMELESS_CELL_PRICE = 44436;

// Вспомогательная функция для поиска связанных стен
const findConnectedWalls = (startWall, allWalls) => {
  const component = new Set();
  const stack = [startWall];
  const visited = new Set();
  const epsilon = 0.01;

  while (stack.length > 0) {
    const currentWall = stack.pop();

    if (visited.has(currentWall.id)) {
      continue;
    }
    visited.add(currentWall.id);
    component.add(currentWall);

    allWalls.forEach((otherWall) => {
      if (currentWall.id === otherWall.id || visited.has(otherWall.id)) {
        return;
      }

      const pointsA = [currentWall.startPoint, currentWall.endPoint];
      const pointsB = [otherWall.startPoint, otherWall.endPoint];

      if (
        pointsA.some((pA) => pointsB.some((pB) => distance(pA, pB) < epsilon))
      ) {
        stack.push(otherWall);
      }
    });
  }

  return Array.from(component);
};

// Проверяет, образуют ли стены замкнутый ортогональный цикл (прямоугольник)
const checkIfWallsFormClosedOrthogonalLoop = (walls) => {
  if (!walls || walls.length < 4) return false;

  const epsilon = 0.01;
  const pointMap = new Map();
  const edgeSet = new Set();

  const getPointKey = (point) => `${point.x.toFixed(2)},${point.y.toFixed(2)}`;

  // Собираем все точки для нахождения границ
  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;

  for (const wall of walls) {
    const { startPoint, endPoint } = wall;
    const dx = Math.abs(startPoint.x - endPoint.x);
    const dy = Math.abs(startPoint.y - endPoint.y);

    const isHorizontal = dy < epsilon;
    const isVertical = dx < epsilon;

    if (!isHorizontal && !isVertical) {
      return false; // диагональная стенка
    }

    // Обновляем границы
    minX = Math.min(minX, startPoint.x, endPoint.x);
    maxX = Math.max(maxX, startPoint.x, endPoint.x);
    minY = Math.min(minY, startPoint.y, endPoint.y);
    maxY = Math.max(maxY, startPoint.y, endPoint.y);

    const startKey = getPointKey(startPoint);
    const endKey = getPointKey(endPoint);

    if (!pointMap.has(startKey)) {
      pointMap.set(startKey, { point: startPoint, connections: new Set() });
    }
    if (!pointMap.has(endKey)) {
      pointMap.set(endKey, { point: endPoint, connections: new Set() });
    }

    // Добавляем ребро в оба направления
    pointMap.get(startKey).connections.add(endKey);
    pointMap.get(endKey).connections.add(startKey);

    const orderedEdgeKey =
      startKey < endKey ? `${startKey}|${endKey}` : `${endKey}|${startKey}`;
    edgeSet.add(orderedEdgeKey);
  }

  // Каждая вершина должна иметь степень 2 для простого замкнутого контура
  for (const node of pointMap.values()) {
    if (node.connections.size !== 2) {
      return false;
    }
  }

  // Проверяем, что все вершины и ребра формируют одну компоненту (один цикл)
  const visitedVertices = new Set();
  const visitedEdges = new Set();
  const keys = Array.from(pointMap.keys());

  const traverse = (currentKey, previousKey = null) => {
    visitedVertices.add(currentKey);
    const node = pointMap.get(currentKey);

    for (const neighborKey of node.connections) {
      const edgeKey =
        currentKey < neighborKey
          ? `${currentKey}|${neighborKey}`
          : `${neighborKey}|${currentKey}`;

      if (!visitedEdges.has(edgeKey)) {
        visitedEdges.add(edgeKey);
        traverse(neighborKey, currentKey);
      }
    }
  };

  if (keys.length === 0) {
    return false;
  }

  traverse(keys[0]);

  // Все вершины должны быть посещены
  if (visitedVertices.size !== pointMap.size) {
    return false;
  }

  // Все ребра должны быть посещены
  if (visitedEdges.size !== edgeSet.size) {
    return false;
  }

  return true;
};

// Находит все замкнутые циклы (комнаты) из стен
const findAllClosedLoops = (walls) => {
  const loops = [];
  const epsilon = 0.001;

  // Вспомогательная функция для получения конечной точки в зависимости от направления
  const getNextPoint = (wall, fromPoint) => {
    const distToStart = distance(fromPoint, wall.startPoint);
    const distToEnd = distance(fromPoint, wall.endPoint);

    if (distToStart < epsilon) {
      return wall.endPoint; // Идём от start к end
    } else if (distToEnd < epsilon) {
      return wall.startPoint; // Идём от end к start
    }
    return null;
  };

  // Пробуем найти все возможные замкнутые циклы из 4 стен (прямоугольники)
  for (let i = 0; i < walls.length; i++) {
    const startWall = walls[i];

    // Пробуем оба направления для начальной стены
    for (const startPoint of [startWall.startPoint, startWall.endPoint]) {
      const visited = new Set([startWall.id]);
      const path = [startWall];
      const currentPoint = getNextPoint(startWall, startPoint);
      if (!currentPoint) continue;

      const dfs = (currentWall, currentEnd) => {
        // Проверяем, можем ли замкнуть цикл
        if (path.length >= 4) {
          const distToStart = distance(currentEnd, startPoint);
          if (distToStart < epsilon) {
            // Нашли замкнутый цикл!
            const loop = [...path];
            // Проверяем, что это ортогональный прямоугольник
            if (checkIfWallsFormClosedOrthogonalLoop(loop)) {
              // Проверяем, не добавили ли мы уже этот цикл
              const loopKey = loop.map(w => w.id).sort().join('|');
              const isDuplicate = loops.some(existingLoop => {
                const existingKey = existingLoop.map(w => w.id).sort().join('|');
                return existingKey === loopKey;
              });
              if (!isDuplicate) {
                loops.push(loop);
              }
            }
            return;
          }
        }

        if (path.length >= 10) return; // Ограничение глубины поиска

        // Ищем следующую стену, которая соединяется с текущей точкой
        for (const nextWall of walls) {
          if (visited.has(nextWall.id)) continue;

          const nextPoint = getNextPoint(nextWall, currentEnd);
          if (nextPoint) {
            visited.add(nextWall.id);
            path.push(nextWall);
            dfs(nextWall, nextPoint);
            path.pop();
            visited.delete(nextWall.id);
          }
        }
      };

      dfs(startWall, currentPoint);
    }
  }

  // Вычисляем границы и площадь каждого цикла
  const loopsWithBounds = loops.map(loop => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    loop.forEach(wall => {
      minX = Math.min(minX, wall.startPoint.x, wall.endPoint.x);
      minY = Math.min(minY, wall.startPoint.y, wall.endPoint.y);
      maxX = Math.max(maxX, wall.startPoint.x, wall.endPoint.x);
      maxY = Math.max(maxY, wall.startPoint.y, wall.endPoint.y);
    });
    const area = (maxX - minX) * (maxY - minY);
    return { loop, minX, minY, maxX, maxY, area };
  });

  // Фильтруем: оставляем только минимальные (не содержащие других циклов)
  const minimalLoops = loopsWithBounds.filter((loopData, index) => {
    const { loop, minX, minY, maxX, maxY, area } = loopData;

    // Проверяем, не содержит ли этот цикл другие циклы по площади
    for (let j = 0; j < loopsWithBounds.length; j++) {
      if (j === index) continue;

      const other = loopsWithBounds[j];

      // Если другой цикл имеет меньшую площадь и находится полностью внутри текущего
      if (other.area < area) {
        const isInside =
          other.minX >= minX - 0.01 &&
          other.maxX <= maxX + 0.01 &&
          other.minY >= minY - 0.01 &&
          other.maxY <= maxY + 0.01;

        if (isInside) {
          return false;
        }
      }
    }

    return true;
  });

  return minimalLoops.map(data => ({
    loop: data.loop,
    area: data.area,
    minX: data.minX,
    minY: data.minY,
    maxX: data.maxX,
    maxY: data.maxY
  }));
};

export const useCostCalculation = (projectState) => {
  const { prices, templates } = usePricing();
  const { t } = useTranslation();

  const calculationResult = useMemo(() => {
    const costBreakdown = {
      modules: { label: t('cost.baseModules'), total: 0 },
      elements: { label: t('cost.additionalOptions'), total: 0 },
    };

    const itemCounts = {};

    if (!projectState || !projectState.projectObjects || !prices || !prices.MODULES || !prices.ELEMENTS) {
      return { totalCost: 0, costBreakdown };
    }

    const wallSegmentPrice = prices.ELEMENTS?.wall_segment || 0;
    // Для безкаркасных стен используем цену internal_wall_tool (1800), а не wall_segment (3688)
    const framelessWallPrice = prices.ELEMENTS?.internal_wall_tool || 0;

    // --- НАЧАЛО ИЗМЕНЕНИЯ: Полностью переработанная логика расчета ---
    // Собираем все модули и стыки со всех этажей
    const allModules = [];
    const allJoints = [];
    for (const floorId in projectState.projectObjects) {
      const floorObjects = projectState.projectObjects[floorId];
      allModules.push(...floorObjects.filter(obj => obj.type === OBJECT_TYPES.MODULE));
      allJoints.push(...floorObjects.filter(obj => obj.type === OBJECT_TYPES.DOCKING_JOINT));
    }

    // Отслеживаем уже посчитанные лестницы (floor1 и floor2 считаются как одна)
    const countedStairs = new Set();

    // 1. Рассчитываем базовую стоимость всех модулей
    let totalModuleCost = 0;
    allModules.forEach(module => {
      // Для лестниц: floor1 и floor2 считаются как одна лестница
      if (module.subtype === 'stairs' && module.templateId) {
        const counterpart = STAIRS_COUNTERPART_MAP[module.templateId];
        // Если уже посчитана соответствующая часть лестницы, пропускаем
        if (counterpart && countedStairs.has(counterpart)) {
          return; // Пропускаем вторую часть лестницы
        }
        // Если текущая лестница уже была посчитана (встретилась раньше), пропускаем
        if (countedStairs.has(module.templateId)) {
          return;
        }
        // Помечаем обе части лестницы как посчитанные
        if (counterpart) {
          countedStairs.add(module.templateId);
          countedStairs.add(counterpart);
        } else {
          // Если нет counterpart (неожиданный templateId), все равно помечаем как посчитанную
          countedStairs.add(module.templateId);
        }
      }

      let baseModulePrice = 420000; // Цена по умолчанию

      if (module.templateId && templates && templates.length > 0) {
        const template = templates.find(t => t.config?.id === module.templateId);
        if (template && template.price && parseFloat(template.price) > 0) {
          baseModulePrice = parseFloat(template.price);
        } else {
          const moduleSubtype = module.subtype || 'residential';
          baseModulePrice = prices.MODULES[moduleSubtype] || prices.MODULES['residential'] || 420000;
        }
      } else {
        const moduleSubtype = module.subtype || 'residential';
        baseModulePrice = prices.MODULES[moduleSubtype] || prices.MODULES['residential'] || 420000;
      }

      // Логика добавления стоимости монтажа (15 000 грн), если модулей больше одного
      if (allModules.length > 1) {
        baseModulePrice += 15000;
      }

      totalModuleCost += baseModulePrice;
    });

    // 2. Применяем скидки за стены на стыках
    allJoints.forEach(joint => {
      const jointLengthM = joint.width;
      const numSegments = Math.round(jointLengthM / GRID_CELL_SIZE_M);

      if (joint.displayMode === 'single') {
        // Убирается одна стена по всей длине стыка
        const discount = numSegments * wallSegmentPrice;
        totalModuleCost -= discount;
      } else if (joint.displayMode === 'passage') {
        // Убираются обе стены по всей длине стыка
        const discount = numSegments * wallSegmentPrice * 2;
        totalModuleCost -= discount;
      }
      // Для 'double' режима скидка не применяется
    });

    // Для модульного режима используем modules.total
    if (totalModuleCost > 0) {
      costBreakdown.modules.total = totalModuleCost;

      // Добавляем информацию о монтаже для отображения в UI
      if (allModules.length > 1) {
        costBreakdown.modules.includesMounting = true;
        costBreakdown.modules.mountingCost = allModules.length * 15000;
      }
    }
    // --- КОНЕЦ ИЗМЕНЕНИЯ ---

    // Собираем все каркасы для расчета их стоимости
    const allFrames = [];
    for (const floorId in projectState.projectObjects) {
      const floorObjects = projectState.projectObjects[floorId];
      allFrames.push(...floorObjects.filter(obj => obj.type === OBJECT_TYPES.FRAME_STRUCTURE));
    }

    // Рассчитываем стоимость каркасов по их цене из базы данных
    let totalFrameCost = 0;
    allFrames.forEach(frame => {
      // Используем цену каркаса из объекта (должна быть сохранена при создании)
      const framePrice = frame.price || 0;
      if (framePrice > 0) {
        totalFrameCost += parseFloat(framePrice);
      }
    });

    // Добавляем стоимость каркасов к базовой стоимости
    if (totalFrameCost > 0) {
      costBreakdown.modules.total += totalFrameCost;
    }

    for (const floorId in projectState.projectObjects) {
      const floorObjects = projectState.projectObjects[floorId];

      floorObjects.forEach(obj => {
        if (obj.type === OBJECT_TYPES.MODULE) {
          Object.entries(obj.wallSegments || {}).forEach(([segmentKey, segmentData]) => {
            const [coords, orientation] = segmentKey.split("_");
            const [cxStr, cyStr] = coords.split(",");
            const cellX = parseInt(cxStr, 10);
            const cellY = parseInt(cyStr, 10);

            const isInternalWall = (orientation === "h" && cellY > 0 && cellY < obj.cellsLong) ||
              (orientation === "v" && cellX > 0 && cellX < obj.cellsWide);

            if (isInternalWall) {
              const elKey = `ELEMENT_${OBJECT_TYPES.INTERNAL_WALL_TOOL}`;
              itemCounts[elKey] = (itemCounts[elKey] || 0) + 1;
            }

            (segmentData.elements || []).forEach(el => {
              const elKey = `ELEMENT_${el.type}`;
              itemCounts[elKey] = (itemCounts[elKey] || 0) + 1;
            });
          });

          (obj.freeElements || []).forEach(el => {
            const elKey = `ELEMENT_${el.type}`;
            itemCounts[elKey] = (itemCounts[elKey] || 0) + 1;
          });

        } else if (obj.type === OBJECT_TYPES.DOCKING_JOINT) {
          (obj.elements || []).forEach(el => {
            const elKey = `ELEMENT_${el.type}`;
            itemCounts[elKey] = (itemCounts[elKey] || 0) + 1;
          });
        } else if (obj.type === OBJECT_TYPES.FRAMELESS_WALL) {
          // Элементы могут быть как напрямую в obj.elements, так и в segments
          (obj.elements || []).forEach(el => {
            const elKey = `ELEMENT_${el.type}`;
            itemCounts[elKey] = (itemCounts[elKey] || 0) + 1;
          });

          // Также проверяем элементы в сегментах
          (obj.segments || []).forEach(segment => {
            (segment.elements || []).forEach(el => {
              const elKey = `ELEMENT_${el.type}`;
              itemCounts[elKey] = (itemCounts[elKey] || 0) + 1;
            });
          });
        } else if (obj.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
          // Для внутренних стен (isInternal: true) считаем стоимость
          // Каждая внутренняя стена - это один сэндвич, поэтому добавляем цену за каждый объект
          if (obj.isInternal === true) {
            // Используем цену internal_wall_tool (1800 грн за сэндвич)
            const elKey = `ELEMENT_${OBJECT_TYPES.INTERNAL_WALL_TOOL}`;
            itemCounts[elKey] = (itemCounts[elKey] || 0) + 1;
          }

          // Для перегородок элементы находятся в segments
          (obj.segments || []).forEach(segment => {
            (segment.elements || []).forEach(el => {
              const elKey = `ELEMENT_${el.type}`;
              itemCounts[elKey] = (itemCounts[elKey] || 0) + 1;
            });
          });
        }
      });
    }

    // Расчет стоимости для безкаркасных проектов (по площади клеток + элементы)
    // Собираем все безкаркасные стены со всех этажей
    const allFramelessWalls = [];
    for (const floorId in projectState.projectObjects) {
      const floorObjects = projectState.projectObjects[floorId];
      allFramelessWalls.push(...floorObjects.filter(obj =>
        obj.type === OBJECT_TYPES.FRAMELESS_WALL || obj.type === OBJECT_TYPES.FRAMELESS_PARTITION
      ));
    }

    // Если есть безкаркасные стены, рассчитываем стоимость по новой логике
    if (allFramelessWalls.length > 0) {
      // Получаем цену за квадратный метр из прайса (или используем значение по умолчанию)
      const framelessSquareMeterPrice = prices.ELEMENTS?.[OBJECT_TYPES.FRAMELESS_SQUARE_METER_PRICE] || DEFAULT_FRAMELESS_CELL_PRICE;

      // Находим все замкнутые циклы (комнаты)
      const closedLoops = findAllClosedLoops(allFramelessWalls.filter(w => w.type === OBJECT_TYPES.FRAMELESS_WALL));

      // Считаем стоимость по площади (клеткам)
      let totalRoomsCost = 0;
      closedLoops.forEach((roomData, index) => {
        const { area } = roomData;
        // Вычисляем количество клеток (квадратных метров)
        // FRAMELESS_GRID_CELL_SIZE_M = 1.0 метр, значит площадь в м² = количество клеток
        const cellsCount = area / (FRAMELESS_GRID_CELL_SIZE_M * FRAMELESS_GRID_CELL_SIZE_M);
        const roomCost = cellsCount * framelessSquareMeterPrice;
        totalRoomsCost += roomCost;
      });

      // Добавляем стоимость комнат к базовой стоимости модулей
      // Для безкаркасных проектов используем modules.total для хранения стоимости комнат
      if (totalRoomsCost > 0) {
        costBreakdown.modules.total += totalRoomsCost;
      }
    }

    // Считаем стоимость элементов
    for (const key in itemCounts) {
      const count = itemCounts[key];
      if (count > 0) {
        if (key.startsWith('ELEMENT_')) {
          const elementType = key.replace('ELEMENT_', '');
          const price = prices.ELEMENTS[elementType] || 0;
          costBreakdown.elements.total += count * price;
        }
      }
    }

    const totalCost = costBreakdown.modules.total + costBreakdown.elements.total;

    return { totalCost, costBreakdown };

  }, [projectState, prices, templates, t]);

  return calculationResult;
};